import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getFavorites, getRecents } from "@/App";
import {
  House, Clock, Star, TrendUp, CurrencyBtc, Book, Globe, Coins, Buildings,
  Swap, Wallet, ListBullets, Lightning, Vault, CaretDown, Crown, List, X,
  LinkSimple, Newspaper, Crosshair, ChatCircleDots, GraduationCap, ChartLineUp, Trash, Egg,
} from "@phosphor-icons/react";

const navItems = [
  { path: "/", label: "Dashboard", icon: House },
  { path: "/forecasting", label: "Forecasting", icon: Crosshair },
  { path: "/live-news", label: "Live News", icon: Newspaper },
  { path: "/economics", label: "Economic Data", icon: ChartLineUp },
  { path: "/academy", label: "Crypto Academy", icon: Book },
  { path: "/financial-education", label: "Financial Education", icon: GraduationCap },
  { path: "/golden-summary", label: "Golden Formula", icon: Crown },
  { path: "/golden-goals", label: "Golden Goals", icon: Egg },
];

const cryptoItems = [
  { path: "/tokens", label: "Tokens", icon: ListBullets },
  { path: "/defillama", label: "DeFi Llama", icon: Vault },
  { path: "/blockchains", label: "Blockchains", icon: Globe },
  { path: "/stablecoins", label: "Stablecoins", icon: Coins },
  { path: "/cex", label: "CEX", icon: Buildings },
  { path: "/dex", label: "DEX", icon: Swap },
  { path: "/wallets", label: "Wallets", icon: Wallet },
  { path: "/dexscreener", label: "DexScreener", icon: Lightning },
  { path: "/misc-links", label: "Resources", icon: LinkSimple },
];

const CRYPTO_PATHS = cryptoItems.map(i => i.path);
const OWNER_EMAIL = "whoshonk@gmail.com";

function NavBtn({ path, label, icon: Icon, onClick, small, currentPath }) {
  const navigate = useNavigate();
  const isActive = currentPath === path;
  return (
    <button
      data-testid={`nav-${path?.slice(1) || label.toLowerCase().replace(/\s+/g, "-")}`}
      onClick={onClick || (() => navigate(path))}
      className={`golden-egg flex items-center gap-1.5 whitespace-nowrap transition-all ${small ? "px-3 py-1 text-[10px]" : "px-3.5 py-1.5 text-[11px]"} ${
        isActive ? "golden-egg-active" : ""
      }`}
    >
      <Icon size={small ? 11 : 13} weight={path === "/golden-summary" ? "fill" : "bold"} className={isActive ? "text-amber-200" : "text-amber-400/70"} />
      <span>{label}</span>
    </button>
  );
}

function SillyGooseBtn({ mobile, currentPath, onNavigate }) {
  const active = currentPath === "/silly-goose";
  if (mobile) {
    return (
      <button
        onClick={onNavigate}
        data-testid="nav-silly-goose-mobile"
        className={`w-full flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded-sm transition-colors ${
          active ? "bg-amber-500/20 text-amber-200" : "text-amber-400/80 hover:text-amber-200 hover:bg-amber-500/10"
        }`}
      >
        <span>🪿</span><span>Silly Goose</span>
      </button>
    );
  }
  return (
    <button
      onClick={onNavigate}
      data-testid="nav-silly-goose"
      title="Silly Goose · Gold Tier"
      className={`golden-egg golden-egg-premium flex items-center gap-1.5 px-3.5 py-1.5 text-[11px] transition-all whitespace-nowrap ${
        active ? "golden-egg-active" : ""
      }`}
    >
      <span className="text-sm leading-none">🪿</span>
      <span>Silly Goose</span>
    </button>
  );
}

export { navItems, cryptoItems };

export default function NavLinks({ menuOpen, setMenuOpen }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cryptoOpen, setCryptoOpen] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [recents, setRecents] = useState([]);
  const cryptoRef = useRef(null);
  const dropdownRef = useRef(null);

  // Refresh favorites/recents + close menus on route change
  useEffect(() => {
    setRecents(getRecents());
    setFavorites(getFavorites());
    setMenuOpen(false);
    setCryptoOpen(false);
  }, [location.pathname, setMenuOpen]);

  // Live favorites sync
  useEffect(() => {
    const onFavChange = () => setFavorites(getFavorites());
    window.addEventListener("favorites-changed", onFavChange);
    return () => window.removeEventListener("favorites-changed", onFavChange);
  }, []);

  // Close crypto dropdown on outside click
  useEffect(() => {
    if (!cryptoOpen) return;
    const handleClick = (e) => {
      if (
        cryptoRef.current && !cryptoRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setCryptoOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [cryptoOpen]);

  const currentPath = location.pathname;
  const isCryptoActive = CRYPTO_PATHS.includes(currentPath);
  const isGold = user?.tier === "gold" || user?.email?.toLowerCase() === OWNER_EMAIL;

  return (
    <>
      {/* Hamburger trigger (mobile only) */}
      <button
        data-testid="menu-toggle"
        onClick={() => setMenuOpen(!menuOpen)}
        className="lg:hidden p-2 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 rounded-sm transition-colors"
      >
        {menuOpen ? <X size={20} weight="bold" /> : <List size={20} weight="bold" />}
      </button>

      {/* Desktop horizontal nav */}
      <div className="hidden lg:block border-t border-zinc-800/50">
        <div className="flex items-center gap-0.5 px-3 py-1 overflow-x-auto scrollbar-hide">
          {navItems.slice(0, 5).map(item => (
            <NavBtn key={item.path} {...item} currentPath={currentPath} />
          ))}

          {/* Crypto dropdown trigger */}
          <div className="relative" ref={cryptoRef}>
            <button
              data-testid="nav-crypto-group"
              onClick={() => setCryptoOpen(!cryptoOpen)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] whitespace-nowrap transition-colors rounded-sm ${
                isCryptoActive ? "bg-zinc-800 text-zinc-50" : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/60"
              }`}
            >
              <CurrencyBtc size={13} weight="bold" />
              <span>Crypto</span>
              <CaretDown size={10} className={`text-zinc-600 transition-transform ${cryptoOpen ? "rotate-180" : ""}`} />
            </button>
          </div>

          {navItems.slice(5).map(item => (
            <NavBtn key={item.path} {...item} currentPath={currentPath} />
          ))}

          {isGold && (
            <SillyGooseBtn currentPath={currentPath} onNavigate={() => navigate("/silly-goose")} />
          )}

          {favorites.length > 0 && (
            <div className="flex items-center gap-0.5 ml-2 pl-2 border-l border-zinc-800/60">
              {favorites.slice(0, 4).map(f => (
                <button
                  key={`fav-${f.type}-${f.id}`}
                  data-testid={`fav-sidebar-${f.symbol?.toLowerCase()}`}
                  onClick={() => navigate(`/asset/${f.type}/${encodeURIComponent(f.id)}${f.chain ? `?chain=${encodeURIComponent(f.chain)}` : ""}`)}
                  className="flex items-center gap-1 px-2 py-1.5 text-[10px] text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/60 rounded-sm transition-colors whitespace-nowrap"
                >
                  <Star size={9} weight="fill" className="text-amber-500/70" />
                  <span>{f.symbol}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mobile menu overlay */}
      {menuOpen && (
        <div className="lg:hidden border-t border-zinc-800/50 bg-zinc-950 max-h-[80vh] overflow-y-auto">
          <nav className="p-2 space-y-0.5">
            {navItems.slice(0, 2).map(item => (
              <NavBtn key={item.path} {...item} currentPath={currentPath} />
            ))}

            {/* Crypto expandable */}
            <button
              data-testid="nav-crypto-group-mobile"
              onClick={() => setCryptoOpen(!cryptoOpen)}
              className={`w-full flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded-sm transition-colors ${
                isCryptoActive ? "bg-zinc-800 text-zinc-50" : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/60"
              }`}
            >
              <CurrencyBtc size={13} weight="bold" />
              <span>Crypto</span>
              <CaretDown size={10} className={`ml-auto text-zinc-600 transition-transform ${cryptoOpen ? "rotate-180" : ""}`} />
            </button>
            {cryptoOpen && (
              <div className="pl-3 space-y-0.5">
                {cryptoItems.map(item => (
                  <NavBtn key={item.path} {...item} currentPath={currentPath} small />
                ))}
              </div>
            )}

            {navItems.slice(2).map(item => (
              <NavBtn key={item.path} {...item} currentPath={currentPath} />
            ))}

            {isGold && (
              <SillyGooseBtn mobile currentPath={currentPath} onNavigate={() => { navigate("/silly-goose"); setMenuOpen(false); }} />
            )}

            {/* Favorites */}
            {favorites.length > 0 && (
              <div className="pt-2 border-t border-zinc-800/50 mt-2">
                <p className="text-[9px] uppercase tracking-wider text-amber-500/60 px-3 mb-1 font-medium flex items-center gap-1">
                  <Star size={9} weight="fill" /> Favorites
                </p>
                {favorites.map(f => (
                  <button
                    key={`fav-m-${f.type}-${f.id}`}
                    onClick={() => navigate(`/asset/${f.type}/${encodeURIComponent(f.id)}${f.chain ? `?chain=${encodeURIComponent(f.chain)}` : ""}`)}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/60 rounded-sm transition-colors"
                  >
                    <Star size={10} weight="fill" className="text-amber-500/70" />
                    <span className="truncate">{f.name}</span>
                    <span className="text-[9px] text-zinc-600 ml-auto">{f.symbol}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Recents */}
            {recents.length > 0 && (
              <div className="pt-2 border-t border-zinc-800/50 mt-2">
                <div className="flex items-center justify-between px-3 mb-1">
                  <p className="text-[9px] uppercase tracking-wider text-zinc-600 font-medium flex items-center gap-1">
                    <Clock size={9} /> Recently Viewed
                  </p>
                  <button
                    onClick={() => { localStorage.removeItem("market_recents"); setRecents([]); }}
                    className="text-[9px] text-zinc-600 hover:text-red-400 transition-colors flex items-center gap-0.5"
                  >
                    <Trash size={9} /> Clear
                  </button>
                </div>
                {recents.map(r => (
                  <button
                    key={`rec-m-${r.type}-${r.id}`}
                    onClick={() => navigate(`/asset/${r.type}/${encodeURIComponent(r.id)}${r.chain ? `?chain=${encodeURIComponent(r.chain)}` : ""}`)}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/60 rounded-sm transition-colors"
                  >
                    {r.type === "crypto" ? <CurrencyBtc size={11} /> : <TrendUp size={11} />}
                    <span className="truncate">{r.name}</span>
                    <span className="text-[9px] text-zinc-600 ml-auto">{r.symbol}</span>
                  </button>
                ))}
              </div>
            )}
          </nav>
          <div className="p-2 border-t border-zinc-800/50">
            <p className="text-[9px] text-zinc-600 text-center">Powered by Grok AI</p>
          </div>
        </div>
      )}

      {/* Desktop crypto dropdown — rendered as fixed overlay to escape overflow:hidden */}
      {cryptoOpen && !menuOpen && cryptoRef.current && (() => {
        const rect = cryptoRef.current.getBoundingClientRect();
        return (
          <div
            ref={dropdownRef}
            className="fixed bg-zinc-900 border border-zinc-700 rounded-sm shadow-2xl py-1 min-w-[170px]"
            style={{ top: rect.bottom + 4, left: rect.left, zIndex: 9999 }}
            data-testid="crypto-dropdown"
          >
            {cryptoItems.map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  data-testid={`nav-${item.path.slice(1)}`}
                  onClick={() => { navigate(item.path); setCryptoOpen(false); }}
                  className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-[11px] transition-colors ${
                    currentPath === item.path ? "bg-zinc-800 text-zinc-50" : "text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800"
                  }`}
                >
                  <Icon size={13} weight="bold" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        );
      })()}
    </>
  );
}
